import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { motion } from 'framer-motion';
import { Plus, MapPin, IndianRupee, Calendar, Building2, X, Upload, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CompanyDashboard = () => {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    pincode: '',
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchListings();
  }, [token]);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const listingData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        location: formData.location,
        pincode: formData.pincode,
      };

      const response = await axios.post(
        `${API_URL}/api/listings`,
        listingData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (selectedImage && imagePreview) {
        const uploadResponse = await axios.post(
          `${API_URL}/api/upload`,
          { image_data: imagePreview },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        await axios.put(
          `${API_URL}/api/listings/${response.data.id}/image?image_url=${encodeURIComponent(uploadResponse.data.image_url)}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      toast.success('Listing created successfully!');
      setShowUploadModal(false);
      resetForm();
      fetchListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      location: '',
      pincode: '',
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)}L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="company-title">
              My Listings
            </h1>
            <p className="text-lg text-slate-600">
              Manage your property listings
            </p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm flex items-center gap-2"
            data-testid="upload-button"
          >
            <Plus className="w-5 h-5" />
            Upload Listing
          </button>
        </motion.div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <div className="text-slate-600">Loading listings...</div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-testid="empty-state">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <div className="text-xl font-semibold text-slate-900 mb-2">No listings yet</div>
            <div className="text-slate-600 mb-6">Create your first property listing</div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Upload Listing
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {listings.map((listing, index) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 smooth-transition group"
                data-testid={`listing-${index}`}
              >
                <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                  {listing.image_url ? (
                    <img
                      src={listing.image_url}
                      alt={listing.title}
                      className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Building2 className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-slate-900 mb-3">
                    {listing.title}
                  </h3>

                  <div className="flex flex-col gap-2 mb-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{listing.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">PIN:</span>
                      <span>{listing.pincode}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(listing.created_at)}</span>
                    </div>
                  </div>

                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                    {listing.description}
                  </p>

                  <div className="text-2xl font-bold text-slate-900">
                    {formatPrice(listing.price)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="upload-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-slate-900">
                Upload New Listing
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600 smooth-transition"
                data-testid="close-modal-button"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Property Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                  placeholder="e.g., Luxury 3BHK Apartment"
                  data-testid="title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                    placeholder="e.g., Andheri West, Mumbai"
                    data-testid="location-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    required
                    pattern="[0-9]{6}"
                    className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                    placeholder="e.g., 400058"
                    data-testid="pincode-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Price (₹)
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  min="0"
                  step="1000"
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                  placeholder="e.g., 5000000"
                  data-testid="price-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition resize-none"
                  placeholder="Describe the property..."
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Property Image
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                    data-testid="image-input"
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-200 rounded-lg hover:border-slate-300 smooth-transition cursor-pointer bg-slate-50"
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <>
                        <ImageIcon className="w-12 h-12 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-600">Click to upload image</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-slate-100 text-slate-700 rounded-lg px-6 py-3 font-medium hover:bg-slate-200 smooth-transition"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-listing-button"
                >
                  {submitting ? 'Creating...' : 'Create Listing'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
